import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIntakeAutoSaveOptions {
  /** The form data to monitor for changes */
  formData: unknown;
  /** Called every time we want to persist the form */
  onSave: () => Promise<unknown>;
  /** When manual save or other parts of the UI are already saving */
  isSavingExternally: boolean;
  /** When the notes mutation is in-flight - prevents race condition with notes save */
  isNoteSaving?: boolean;
  /** When the form data is still loading - wait for this to be false before tracking changes */
  isLoading?: boolean;
  /** When the form has been populated with server data - wait for this before tracking changes */
  isFormHydrated?: boolean;
  /** Debounce delay in ms (default 3000) */
  debounceMs?: number;
  /** Interval in ms for periodic saves (default 30000) */
  intervalMs?: number;
  /** Whether autosave is enabled (default true) */
  enabled?: boolean;
}

interface UseIntakeAutoSaveReturn {
  /** Whether autosave is currently in progress */
  isAutoSaving: boolean;
  /** Timestamp of last successful save */
  lastSavedAt: Date | null;
  /** Error message if autosave failed */
  autoSaveError: string | null;
  /** Whether there are unsaved changes (including during debounce) */
  hasUnsavedChanges: boolean;
  /** Number of consecutive save errors */
  errorCount: number;
  /** Manually trigger a save immediately (for blur events) */
  saveNow: () => Promise<void>;
  /** Cancel any pending debounced save without triggering one */
  cancelPendingSave: () => void;
}

/**
 * Hook for autosaving form data with debouncing and dirty tracking.
 *
 * Features:
 * - Debounced saves when user stops typing
 * - Periodic backup saves (every 30s if there are changes)
 * - Tracks unsaved changes state for UI indicators and navigation warnings
 * - Syncs with external saves (manual save button)
 */
export const useIntakeAutoSave = ({
  formData,
  onSave,
  isSavingExternally,
  isNoteSaving = false,
  isLoading = false,
  isFormHydrated = true,
  debounceMs = 3000,
  intervalMs = 30000,
  enabled = true,
}: UseIntakeAutoSaveOptions): UseIntakeAutoSaveReturn => {
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Track the last saved form data string to detect changes
  const lastSavedFormRef = useRef<string>(JSON.stringify(formData));
  // Track current form data string for comparison
  const currentFormRef = useRef<string>(JSON.stringify(formData));
  // Track if we have pending changes (for debounce)
  const hasPendingChangesRef = useRef(false);
  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Interval timer ref
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track previous external saving state
  const prevIsSavingExternallyRef = useRef(isSavingExternally);
  // Track previous note saving state
  const prevIsNoteSavingRef = useRef(isNoteSaving);
  // Track previous loading state
  const prevIsLoadingRef = useRef(isLoading);
  // Track if we've initialized (to avoid showing unsaved on first load)
  const hasInitializedRef = useRef(false);

  // Keep references updated synchronously during render
  // to avoid stale closures when timers fire between state update and effect
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const isSavingExternallyRef = useRef(isSavingExternally);
  isSavingExternallyRef.current = isSavingExternally;
  const isNoteSavingRef = useRef(isNoteSaving);
  isNoteSavingRef.current = isNoteSaving;

  // Reset initialization when loading starts or form becomes un-hydrated
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current;
    const startedLoading = !wasLoading && isLoading;

    if (startedLoading || !isFormHydrated) {
      hasInitializedRef.current = false;
    }

    prevIsLoadingRef.current = isLoading;
  }, [isLoading, isFormHydrated]);

  // Update current form ref and check for changes
  useEffect(() => {
    const formString = JSON.stringify(formData);
    currentFormRef.current = formString;

    // Wait for loading to complete AND form to be hydrated before initializing
    // This prevents treating the initial data load as "unsaved changes"
    if (isLoading || !isFormHydrated) {
      return;
    }

    // On first render after loading + hydration, capture initial state and don't show as unsaved
    if (!hasInitializedRef.current) {
      lastSavedFormRef.current = formString;
      hasInitializedRef.current = true;
      setHasUnsavedChanges(false);
      return;
    }

    const isChanged = formString !== lastSavedFormRef.current;
    hasPendingChangesRef.current = isChanged;
    setHasUnsavedChanges(isChanged);
  }, [formData, isLoading, isFormHydrated]);

  // Perform the actual save
  // Uses refs for external guards so that debounce/interval timer closures
  // always check the LATEST values, not stale ones from the render when the
  // timer was created.  This closes the Safari timing gap where a debounce
  // callback fires before React re-renders with isNoteSaving=true.
  const performSave = useCallback(async (): Promise<boolean> => {
    if (isSavingExternallyRef.current || isAutoSaving || isNoteSavingRef.current) {
      return false;
    }

    const currentFormStr = currentFormRef.current;

    // Double-check there are still changes
    if (currentFormStr === lastSavedFormRef.current) {
      return true;
    }

    setIsAutoSaving(true);
    setAutoSaveError(null);

    try {
      await onSaveRef.current();
      setLastSavedAt(new Date());
      setAutoSaveError(null);
      setErrorCount(0);
      lastSavedFormRef.current = currentFormStr;
      hasPendingChangesRef.current = false;
      setHasUnsavedChanges(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Error saving';
      setAutoSaveError(errorMessage);
      setErrorCount((prev) => prev + 1);
      return false;
    } finally {
      setIsAutoSaving(false);
    }
  }, [isAutoSaving]);

  // Cancel any pending debounce without triggering a save.
  // Call this synchronously before a competing save (e.g. addGeneralNote)
  // to prevent the debounce timer from firing with stale form data.
  const cancelPendingSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Immediate save (for blur events) - clears debounce and saves now
  const saveNow = useCallback(async () => {
    cancelPendingSave();
    await performSave();
  }, [cancelPendingSave, performSave]);

  // Debounced auto-save effect
  useEffect(() => {
    // Clear existing timer when form changes
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Only schedule save if enabled, form is hydrated, we have pending changes, and aren't already saving
    if (
      enabled &&
      isFormHydrated &&
      hasPendingChangesRef.current &&
      !isSavingExternally &&
      !isAutoSaving &&
      !isNoteSaving
    ) {
      debounceTimerRef.current = setTimeout(() => {
        // Double-check we still have changes before saving
        if (currentFormRef.current !== lastSavedFormRef.current) {
          performSave();
        }
        debounceTimerRef.current = null;
      }, debounceMs);
    }

    return (): void => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [formData, enabled, isFormHydrated, isSavingExternally, isAutoSaving, isNoteSaving, debounceMs, performSave]);

  // Watch for external saves (manual save button) and sync our state
  useEffect(() => {
    const wasSaving = prevIsSavingExternallyRef.current;
    const justFinishedSaving = wasSaving && !isSavingExternally;
    prevIsSavingExternallyRef.current = isSavingExternally;

    if (justFinishedSaving) {
      // External save completed - sync our ref to current form data
      lastSavedFormRef.current = currentFormRef.current;
      setLastSavedAt(new Date());
      setHasUnsavedChanges(false);
      hasPendingChangesRef.current = false;
      setAutoSaveError(null);
      setErrorCount(0);

      // Clear any pending auto-save
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [isSavingExternally]);

  // Watch for note saves completing and sync our state
  // This prevents race conditions where autosave was in-flight when note save started
  useEffect(() => {
    const wasNoteSaving = prevIsNoteSavingRef.current;
    const justFinishedNoteSaving = wasNoteSaving && !isNoteSaving;
    prevIsNoteSavingRef.current = isNoteSaving;

    if (justFinishedNoteSaving) {
      // Note save completed - sync our ref to current form data
      // This ensures we don't overwrite the newly saved notes with stale data
      lastSavedFormRef.current = currentFormRef.current;
      setLastSavedAt(new Date());
      setHasUnsavedChanges(false);
      hasPendingChangesRef.current = false;

      // Clear any pending auto-save that might have stale data
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [isNoteSaving]);

  // Periodic backup save
  useEffect(() => {
    if (!enabled || !isFormHydrated) return undefined;

    intervalTimerRef.current = setInterval(() => {
      if (
        hasPendingChangesRef.current &&
        !isSavingExternally &&
        !isAutoSaving &&
        !isNoteSaving
      ) {
        performSave();
      }
    }, intervalMs);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [enabled, isFormHydrated, intervalMs, isSavingExternally, isAutoSaving, isNoteSaving, performSave]);

  // Beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentFormRef.current !== lastSavedFormRef.current) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  return {
    isAutoSaving,
    lastSavedAt,
    autoSaveError,
    hasUnsavedChanges,
    errorCount,
    saveNow,
    cancelPendingSave,
  };
};

export default useIntakeAutoSave;
